import React, { useState, useEffect } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import searchIcon from '../../assets/icons/search.svg';
import { useAuth } from '@/contexts/AuthContext';
import { INPUT_THEMES, type InputTheme } from './form/inputThemes';

export interface Student {
  id: string;
  full_name: string;
  username: string;
  profile_picture_url?: string;
  belt_rank?: string;
}

interface StudentAutocompleteProps {
  value: Student | string | null;
  onChange: (student: Student | string | null) => void;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  disabled?: boolean;
  /** Visual theme: 'dark' or 'light'. Defaults to 'dark'. */
  theme?: InputTheme;
}

export function StudentAutocomplete({
  value,
  onChange,
  placeholder = "Type a name or a username",
  className = "",
  containerClassName = "",
  disabled = false,
  theme = 'dark'
}: StudentAutocompleteProps) {
  const { user } = useAuth();
  const tk = INPUT_THEMES[theme];
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [showManualOption, setShowManualOption] = useState(false);

  // Extract display value from the value prop
  const displayValue = typeof value === 'string' ? value : (value && typeof value === 'object' ? value.full_name : '');

  useEffect(() => {
    setInputValue(displayValue || '');
  }, [displayValue]);

  // Reset input when value becomes null (after student selection)
  useEffect(() => {
    if (value === null) {
      setInputValue('');
      setOpen(false);
      setShowManualOption(false);
    }
  }, [value]);

  useEffect(() => {
    const fetchStudents = async () => {
      const safeInputValue = inputValue || '';
      if (!safeInputValue || safeInputValue.length < 2 || !user) {
        setFilteredStudents([]);
        setShowManualOption(false);
        return;
      }

      setLoading(true);
      try {
        const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
        const token = session?.access_token;

        if (!token) {
          console.error('❌ [StudentAutocomplete] No token available');
          setFilteredStudents([]);
          setShowManualOption(false);
          setLoading(false);
          return;
        }

        const { getEdgeFunctionUrl } = await import('@/utils/url');
        const functionUrl = getEdgeFunctionUrl('search_partners');

        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            searchTerm: safeInputValue,
            limit: 10
          })
        });

        if (response.ok) {
          const result = await response.json();
          const students = result.data || [];
          setFilteredStudents(students);

          const hasExactMatch = students.some(
            (s: Student) =>
              (s.full_name?.toLowerCase() === inputValue.toLowerCase()) ||
              (s.username?.toLowerCase() === inputValue.toLowerCase())
          );
          setShowManualOption(!hasExactMatch && safeInputValue.length > 0);
        } else {
          const errorText = await response.text();
          console.error('❌ [StudentAutocomplete] Error fetching students:', response.status, errorText);
          setFilteredStudents([]);
          setShowManualOption(false);
        }
      } catch (error) {
        console.error('❌ [StudentAutocomplete] Error fetching students:', error);
        setFilteredStudents([]);
        setShowManualOption(false);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchStudents, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue, user]);

  const handleSelect = (student: Student | string) => {
    onChange(student);
    setInputValue(typeof student === 'string' ? student : student.full_name || '');
    setOpen(false);
    setShowManualOption(false);
  };

  const handleManualAdd = () => {
    const safeInputValue = inputValue || '';
    onChange(safeInputValue);
    setInputValue(safeInputValue);
    setOpen(false);
    setShowManualOption(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value || '';
    setInputValue(newValue);
    setOpen(true);
  };

  const handleInputFocus = () => {
    setOpen(true);
  };

  const handleInputBlur = () => {
    // 200ms delay so click events on dropdown items fire before blur closes the list
    setTimeout(() => setOpen(false), 200);
  };

  const safeFilteredStudents = filteredStudents || [];
  const safeInputValue = inputValue || '';
  const dropdownVisible = open && safeInputValue.length > 0 && (safeFilteredStudents.length > 0 || showManualOption);

  return (
    <div className={cn("relative", containerClassName)}>
      <div className="relative">
        <img
          src={searchIcon}
          alt="Search"
          className={cn(
            "absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5",
            theme === 'light'
              ? "brightness-0 opacity-40"
              : "text-white/60"
          )}
        />
        <input
          type="text"
          value={safeInputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "w-full border rounded-lg pl-12 pr-12 text-xs focus:outline-none transition-all duration-200 font-sans h-11",
            tk.input.split(' ').filter(cls => !cls.startsWith('h-') && !cls.startsWith('text-') && !cls.startsWith('font-')).join(' '),
            className
          )}
          style={{ zIndex: 1, borderWidth: '1px', borderStyle: 'solid' }}
        />
        {safeInputValue && (
          <button
            onMouseDown={(e) => {
              // Prevent blur from firing before click clears the value
              e.preventDefault();
              setInputValue('');
              onChange(null);
            }}
            className={cn(
              "absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200",
              theme === 'light'
                ? "text-gray-400 hover:text-gray-600"
                : "text-white/60 hover:text-white"
            )}
            aria-label="Clear search"
            style={{ zIndex: 2 }}
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {loading && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ zIndex: 2 }}>
            <div className={cn(
              "w-4 h-4 border-2 rounded-full animate-spin",
              theme === 'light'
                ? "border-gray-300 border-t-gray-600"
                : "border-white/30 border-t-white"
            )} />
          </div>
        )}
      </div>

      {dropdownVisible && (
        <div
          className={cn('absolute z-50 w-full mt-1 rounded-md shadow-lg max-h-60 overflow-y-auto', tk.dropdown)}
          data-student-dropdown
          data-testid="student-dropdown"
        >
          <div className="py-1">
            {safeFilteredStudents.map((student) => (
              <div
                key={student.id}
                onMouseDown={(e) => {
                  // Prevent blur from closing dropdown before selection fires
                  e.preventDefault();
                  handleSelect(student);
                }}
                className={cn(
                  "relative flex w-full select-none items-center rounded-sm py-1.5 pl-8 pr-2",
                  tk.dropdownItem
                )}
              >
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  {safeInputValue === student.full_name && (
                    <Check className={cn("h-4 w-4", theme === 'light' ? "text-gray-600" : "text-white")} />
                  )}
                </span>
                <div className="flex flex-col flex-1">
                  <span className={cn("text-sm font-medium", tk.dropdownItemText)}>{student.full_name || 'Unknown'}</span>
                  <span className={cn("text-xs", tk.helperText)}>@{student.username || 'unknown'}</span>
                </div>
              </div>
            ))}

            {showManualOption && (
              <div
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleManualAdd();
                }}
                className={cn(
                  "relative flex w-full select-none items-center rounded-sm py-1.5 pl-8 pr-2",
                  "border-t",
                  theme === 'light' ? "border-gray-200" : "border-white/20",
                  tk.dropdownItem
                )}
              >
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  <Plus className={cn("h-4 w-4", theme === 'light' ? "text-gray-600" : "text-white")} />
                </span>
                <div className="flex flex-col flex-1">
                  <span className={cn("text-sm font-medium", tk.dropdownItemText)}>
                    Add "{safeInputValue}" manually
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
